import { networkClient } from './client';
import type { ApiEnvelope, ApiPaginatedCollection } from './types';
import type { BELeague, BELeagueEntry } from '@/lib/leagueMappers';

// ─── Shared types ────────────────────────────────────────────────────────────
export interface FetchLeaguesParams {
  page?: number;
  limit?: number;
}

export interface FetchJoinableLeaguesParams {
  page?: number;
  limit?: number;
  isCelebrity?: boolean;
}

export interface TournamentCalendar {
  id: string;
  competition_name: string | null;
  season_label: string | null;
}

export interface JoinLeaguePayload {
  code: string;
  user_id: string;
  fantasy_team_id: string;
}

export interface CreateLeaguePayload {
  name: string;
  description?: string;
  is_public: boolean;
  start_gameweek: number;
  tournament_calendar_id: string;
}

export interface UpdateLeaguePayload {
  name?: string;
  description?: string;
  is_public?: boolean;
}

// One edition of a league's regeneration chain, newest first
export interface LeagueHistoryItem {
  id: string;
  name: string;
  tournament_calendar_id: string | null;
  competition_name: string | null;
  season_label: string | null;
}

// ─── Response types ───────────────────────────────────────────────────────────

export type MyLeaguesResponse = ApiEnvelope<ApiPaginatedCollection<BELeague>>;
export type LeagueDetailResponse = ApiEnvelope<BELeague>;
export type LeagueEntriesResponse = ApiEnvelope<ApiPaginatedCollection<BELeagueEntry>>;
export type JoinableLeaguesResponse = ApiEnvelope<ApiPaginatedCollection<BELeague>>;
export type LeagueByCodeResponse = ApiEnvelope<BELeague>;
export type JoinLeagueResponse = ApiEnvelope<{
  entry: Record<string, unknown>;
  league: { id: string };
}>;
export type CreateLeagueResponse = ApiEnvelope<{ id: string }>;
export type TournamentCalendarsResponse = ApiEnvelope<TournamentCalendar[]>;
export type LeagueHistoryResponse = ApiEnvelope<LeagueHistoryItem[]>;

// ─── API functions ────────────────────────────────────────────────────────────

export const fetchMyLeagues = (params: FetchLeaguesParams): Promise<MyLeaguesResponse> =>
  networkClient
    .get<MyLeaguesResponse>('/api/league/me', {
      params,
    })
    .then(({ data }) => data);

export const fetchLeagueDetail = (leagueId: string): Promise<LeagueDetailResponse> =>
  networkClient.get<LeagueDetailResponse>(`/api/league/${leagueId}`).then(({ data }) => data);

export const fetchLeagueHistory = (leagueId: string): Promise<LeagueHistoryResponse> =>
  networkClient
    .get<LeagueHistoryResponse>(`/api/league/${leagueId}/history`)
    .then(({ data }) => data);

export const fetchLeagueEntries = (leagueId: string): Promise<LeagueEntriesResponse> =>
  networkClient
    .get<LeagueEntriesResponse>(`/api/league/${leagueId}/entries`, {
      params: { expand: 'user,fantasy_team', limit: 100 },
    })
    .then(({ data }) => data);

export const fetchJoinableLeagues = (
  params: FetchJoinableLeaguesParams
): Promise<JoinableLeaguesResponse> =>
  networkClient
    .get<JoinableLeaguesResponse>('/api/league/joinable', {
      params: {
        page: params.page,
        limit: params.limit,
        is_celebrity: params.isCelebrity,
      },
    })
    .then(({ data }) => data);

export const fetchLeagueByCode = (code: string): Promise<LeagueByCodeResponse> =>
  networkClient
    .get<LeagueByCodeResponse>(`/api/league/by-code/${encodeURIComponent(code)}`)
    .then(({ data }) => data)
    .catch(error => {
      throw error;
    });

export const joinLeague = (payload: JoinLeaguePayload): Promise<JoinLeagueResponse> =>
  networkClient.post<JoinLeagueResponse>('/api/league/join', payload).then(({ data }) => data);

export const createLeague = (payload: CreateLeaguePayload): Promise<CreateLeagueResponse> =>
  networkClient
    .post<CreateLeagueResponse>('/api/league', { ...payload, type: 'mini_league' })
    .then(({ data }) => data);

export const updateLeague = (
  id: string,
  payload: UpdateLeaguePayload
): Promise<LeagueDetailResponse> =>
  networkClient.patch<LeagueDetailResponse>(`/api/league/${id}`, payload).then(({ data }) => data);

export const regenerateLeague = (
  leagueId: string,
  startGameweek: number
): Promise<CreateLeagueResponse> =>
  networkClient
    .post<CreateLeagueResponse>(`/api/league/${leagueId}/regenerate`, {
      start_gameweek: startGameweek,
    })
    .then(({ data }) => data);

export const recordCompletedLeagueShare = (
  leagueId: string,
  method: 'copy_code' | 'copy_link' | 'native_share'
): Promise<void> =>
  networkClient
    .post(`/api/league/${leagueId}/share-completed`, { method })
    .then((): void => undefined);

export const removeLeagueEntry = (leagueId: string, entryId: string): Promise<void> =>
  networkClient.delete(`/api/league/${leagueId}/entries/${entryId}`).then((): void => undefined);

export const fetchTournamentCalendars = (): Promise<TournamentCalendarsResponse> =>
  networkClient
    .get<TournamentCalendarsResponse>('/api/league/tournament-calendars')
    .then(({ data }) => data);
